import { type ReactNode } from 'react';
import { type CardShadow } from '@/primer/components';
import { cn } from '@/utils/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * ScreenCard component props.
 *
 * Sealed — `className` is not exposed. Layout adjustments belong on the parent
 * container. If a new visual variant is needed, add a prop here.
 */
export interface ScreenCardProps {
  children: ReactNode;
  /**
   * Shadow depth applied on `md+` viewports (desktop/tablet).
   * On mobile, no shadow is applied — the card is flush to the viewport edges.
   * @see CardShadow
   */
  shadow?: CardShadow;
}

// ─── Shadow map ───────────────────────────────────────────────────────────────

const shadowMap: Record<CardShadow, string> = {
  ground: 'md:shadow-(--shadow-top-light-ground-floor)',
  first: 'md:shadow-(--shadow-top-light-first-floor)',
  second: 'md:shadow-(--shadow-top-light-second-floor)',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ScreenCard — app-level layout surface for full-screen pre-auth pages.
 *
 * Handles the responsive "full-bleed on mobile, constrained card on desktop"
 * pattern used by screens like the Invite screen, where content must fill the
 * full viewport width on small screens while appearing as a floating card on
 * larger viewports.
 *
 * ### Responsive behaviour
 * - **Mobile** (`< md`): full viewport width, top corners rounded, no horizontal
 *   margin, no bottom radius. The surface bleeds to the screen edges, giving the
 *   illusion that the gradient header flows into the content area.
 * - **Desktop / tablet** (`md+`): centred, width-constrained, fully rounded,
 *   with optional shadow.
 *
 * ### Usage
 * Use `ScreenCard` for full-bleed pre-auth screens. For Login/SignUp-style
 * floating cards, use `Card` directly with a `max-w-sm md:max-w-md` wrapper.
 *
 * @example
 * <ScreenCard shadow="second">
 *   <div className="p-6">…</div>
 * </ScreenCard>
 */
export function ScreenCard({ children, shadow }: ScreenCardProps) {
  return (
    <div
      className={cn(
        // Surface
        'bg-surface-translucent',
        // Border
        'border border-default',
        // Mobile: full bleed, only top corners rounded, stretch to fill remaining height
        'rounded-t-large flex-1',
        // Desktop: fully rounded, centred, width-constrained, no stretch
        'md:rounded-large md:mx-auto md:max-w-sm lg:max-w-md md:flex-none',
        // Shadow — desktop/tablet only
        shadow && shadowMap[shadow]
      )}
      data-testid="screen-card"
    >
      {children}
    </div>
  );
}

export default ScreenCard;
